import { useReducer } from "react";

import {
  ExamQuestionXpBreakdown,
  ExamQuestionXpEventData,
  Reward,
  UserLevel
} from "@seneca/xp-service-types";

export type QuestionXp = {
  questionId: string;
  xp: number;
  breakdown: ExamQuestionXpBreakdown;
};

export type EqSessionState = {
  sessionId: string;
  sessionXp: QuestionXp[];
  totalUserXp: number;
  dailyUserXp: number;
  questionIds: string[];
  userLevel: UserLevel;
  mockId?: string;
  reward?: Reward;
};

export enum EqSessionActionType {
  START_SESSION = "START_SESSION",
  UPDATE_SESSION_XP = "UPDATE_SESSION_XP",
  END_SESSION = "END_SESSION"
}

export type EqSessionAction = {
  type: EqSessionActionType;
  payload: any;
};

export const initialEqSessionState: EqSessionState = {
  sessionId: "",
  sessionXp: [],
  questionIds: [],
  totalUserXp: 0,
  dailyUserXp: 0,
  userLevel: {
    level: 0,
    currentLevelXpThreshold: 0,
    nextLevelXpThreshold: 100
  },
  mockId: ""
};

export function eqSessionReducer(
  state: EqSessionState,
  action: EqSessionAction
) {
  const { type, payload } = action;

  switch (type) {
    case EqSessionActionType.START_SESSION:
      const { sessionId, questionIds, mockId } = payload;

      return {
        ...initialEqSessionState,
        sessionId,
        questionIds,
        mockId
      };
    case EqSessionActionType.UPDATE_SESSION_XP: {
      const {
        questionId,
        xp,
        totalUserXp,
        dailyUserXp,
        reward,
        userLevel,
        breakdown
      } = payload;

      const isValidQuestion = state.questionIds.includes(questionId);

      if (isValidQuestion) {
        return {
          ...state,
          sessionXp: [...state.sessionXp, { questionId, xp, breakdown }],
          totalUserXp,
          dailyUserXp,
          userLevel,
          ...(reward ? { reward } : {})
        } as EqSessionState;
      } else {
        return state;
      }
    }
    case EqSessionActionType.END_SESSION:
      return initialEqSessionState;
    default:
      return state;
  }
}

export const startEqSession = ({
  sessionId,
  questionIds,
  mockId
}: {
  sessionId: string;
  questionIds: string[];
  mockId?: string;
}) => ({
  type: EqSessionActionType.START_SESSION,
  payload: {
    sessionId,
    questionIds,
    mockId
  }
});

export type UpdateSessionXpPayload = ExamQuestionXpEventData;

export const updateSessionXp = (payload: UpdateSessionXpPayload) => ({
  type: EqSessionActionType.UPDATE_SESSION_XP,
  payload: {
    xp: payload.newXp,
    ...payload
  }
});

export const endEqSession = () => ({
  type: EqSessionActionType.END_SESSION,
  payload: {}
});

export default function useEqSessionReducer() {
  return useReducer(eqSessionReducer, initialEqSessionState);
}
